import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../../components/Card';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import { db, auth } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { updatePassword } from 'firebase/auth';
import { motion } from 'framer-motion';

export default function MemberDashboard() {
    const { user } = useAuth();
    const t = useT();
    const isRTL = t.language === 'sindhi';
    
    const [writings, setWritings] = useState([]);
    const [branchActivities, setBranchActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            if (!user) return;
            setLoading(true);
            try {
                // 1. Load "My Writings" (contributions from events)
                // Fetch all events to ensure we get all contributions across all branches/global
                const eventsSnap = await getDocs(collection(db, 'events'));
                const allEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                const myWritings = [];
                const branchActivities = [];

                allEvents.forEach(ev => {
                    // Check for contributions
                    if (ev.contributions) {
                        ev.contributions.forEach(c => {
                            if (c.memberId === user.id || c.memberId === user.memberId) {
                                myWritings.push({ ...c, eventTitle: isRTL ? (ev.titleSindhi || ev.title) : ev.title, eventId: ev.id });
                            }
                        });
                    }

                    // For branch activities, only show events from their branch
                    if (ev.branch === user.branchId) {
                        branchActivities.push(ev);
                    }
                });

                setWritings(myWritings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                setBranchActivities(branchActivities.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10));

            } catch (err) {
                console.error("Error loading member dashboard:", err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user, isRTL]);

    const hour = new Date().getHours();
    const greeting = hour < 12 ? t('goodMorning') : hour < 17 ? t('goodAfternoon') : t('goodEvening');

    return (
        <div className="fade-in" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            {/* Welcome Section */}
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} style={{
                background: 'linear-gradient(135deg, var(--sindhi-navy-mid), var(--sindhi-navy))',
                borderRadius: 32, padding: '48px', marginBottom: 40, color: 'white', position: 'relative', overflow: 'hidden',
                boxShadow: '0 30px 60px -15px rgba(15, 23, 42, 0.3)'
            }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4v-4H4v4H0v2h4v4h2v-4h4v-2H6zm30 0v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ fontSize: 16, color: 'var(--sindhi-gold)', marginBottom: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2 }}>{greeting}</div>
                    <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 44, fontWeight: 800, marginBottom: 12, letterSpacing: -1.5 }}>
                        {t('hello')}, {user?.displayName || user?.name} <span style={{ display: 'inline-block' }}>👋</span>
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, background: 'rgba(255,255,255,0.1)', color: 'white', padding: '6px 16px', borderRadius: 100, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, border: '1px solid rgba(255,255,255,0.1)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                            {isRTL ? 'ميمبر' : 'Member'}
                        </span>
                        {user?.recovery_key && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', padding: '6px 16px', borderRadius: 100 }}>
                                <span style={{ fontSize: 10, color: 'var(--sindhi-gold)', fontWeight: 800, textTransform: 'uppercase' }}>{t('recoveryKey')}:</span>
                                <span style={{ fontSize: 12, color: 'white', fontWeight: 700, fontFamily: 'monospace' }}>{user.recovery_key}</span>
                                <button onClick={() => { navigator.clipboard.writeText(user.recovery_key); toast.success('Copied!'); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 0, display: 'flex' }}>📋</button>
                            </div>
                        )}
                        <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'پنهنجي ادبي سفر ۾ خوش آمديد' : 'Welcome to your literary journey'}</span>
                    </div>
                </div>
            </motion.div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 32 }}>
                    
                    {/* My Writings Section */}
                    <Card style={{ padding: 40, background: 'white', border: '1px solid var(--sindhi-cream)', borderRadius: 32 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                            <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--sindhi-navy)' }}>
                                {t('myWritings')}
                            </h2>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--sindhi-ivory)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✍️</div>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxHeight: 600, overflowY: 'auto', paddingRight: 10 }}>
                            {writings.length > 0 ? writings.map((w, idx) => (
                                <motion.div key={idx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }} style={{
                                    padding: 24, background: 'var(--sindhi-ivory)', borderRadius: 20, border: '1px solid var(--sindhi-cream)', position: 'relative'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-maroon)', textTransform: 'uppercase' }}>{w.type}</span>
                                        <span style={{ fontSize: 11, color: 'var(--sindhi-text-muted)' }}>{new Date(w.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--sindhi-navy)', marginBottom: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{w.eventTitle}</div>
                                    <p style={{ fontSize: 14, color: 'var(--sindhi-text)', lineHeight: 1.6, margin: 0, opacity: 0.8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{w.content}</p>
                                    <div style={{ marginTop: 16, fontSize: 11, color: 'var(--sindhi-gold)', fontWeight: 700 }}>
                                        🔒 {isRTL ? 'لکڻي محفوظ ٿيل آهي (تبديلي نٿي ڪري سگهجي)' : 'Locked (Read-only)'}
                                    </div>
                                </motion.div>
                            )) : (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                    {t('noWritingsYet')}
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Meetings & Activities Section */}
                    <Card style={{ padding: 40, background: 'white', border: '1px solid var(--sindhi-cream)', borderRadius: 32 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--sindhi-navy)' }}>
                                {t('meetingsActivities')}
                            </h2>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--sindhi-ivory)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📢</div>
                        </div>
                        <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', marginBottom: 32, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                            {t('trackMeetingsDesc')}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {branchActivities.length > 0 ? branchActivities.map((ev, idx) => (
                                <motion.div key={ev.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }} style={{
                                    padding: 24, background: 'white', borderRadius: 20, border: '1px solid var(--sindhi-cream)', boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}>
                                            <span style={{ fontSize: 10, background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', padding: '4px 10px', borderRadius: 6, fontWeight: 800, textTransform: 'uppercase' }}>{ev.type}</span>
                                            <span style={{ fontSize: 12, color: 'var(--sindhi-maroon)', fontWeight: 800 }}>{ev.date}</span>
                                        </div>
                                        <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                            {isRTL ? (ev.titleSindhi || ev.title) : ev.title}
                                        </h3>
                                        <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)', display: 'flex', gap: 16 }}>
                                            <span><strong>{t('branch')}:</strong> {ev.branchName || '—'}</span>
                                            <span><strong>{t('members')}:</strong> {ev.attendees?.length || 0}</span>
                                        </div>
                                    </div>

                                    <Link to={`/events/${ev.id}`} style={{ textDecoration: 'none' }}>
                                        <button style={{ 
                                            background: 'var(--sindhi-navy)', color: 'white', border: 'none', 
                                            padding: '10px 20px', borderRadius: 12, fontWeight: 700, 
                                            cursor: 'pointer', fontSize: 13, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                                            boxShadow: '0 4px 12px rgba(10,25,47,0.1)'
                                        }}>
                                            {isRTL ? 'تفصيل ڏسو' : 'View Details'}
                                        </button>
                                    </Link>
                                </motion.div>
                            )) : (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                    {isRTL ? 'ڪا به تازي سرگرمي ناهي.' : 'No recent activity.'}
                                </div>
                            )}
                        </div>
                    </Card>

                </div>
            )}

            {/* Change Password & Account Settings */}
            <div style={{ marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 32 }}>
                <Card style={{ padding: 40, background: 'white', border: '1px solid var(--sindhi-cream)', borderRadius: 32 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                        <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--sindhi-navy)' }}>
                            {t('changePassword')}
                        </h2>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--sindhi-ivory)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔐</div>
                    </div>
                    
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        const newP = e.target.newPassword.value;
                        if (newP.length < 6) {
                            toast.error(isRTL ? "پاسورڊ گھٽ ۾ گھٽ 6 اکرن جو ھجڻ گھرجي." : "Password must be at least 6 characters.");
                            return;
                        }
                        try {
                            await updatePassword(auth.currentUser, newP);
                            toast.success(t('passwordUpdated'));
                            e.target.reset();
                        } catch (err) {
                            if (err.code === 'auth/requires-recent-login') {
                                toast.error(isRTL ? "مهرباني ڪري ٻيهر لاگ ان ٿيو پاسورڊ تبديل ڪرڻ لاءِ." : "Please re-login to change your password.");
                            } else {
                                toast.error(t('error'));
                            }
                        }
                    }}>
                        <div className="form-group">
                            <label className="form-label">{t('newPassword')}</label>
                            <input name="newPassword" type="password" className="form-input" placeholder="••••••••" required />
                        </div>
                        <button type="submit" className="btn btn-primary w-100" style={{ marginTop: 20 }}>
                            {t('updatePassword')}
                        </button>
                    </form>
                </Card>

                {/* System Stats / Security Note */}
                <Card style={{ padding: 40, background: 'var(--sindhi-navy)', color: 'white', border: 'none', borderRadius: 32, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, opacity: 0.1, backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")` }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800, marginBottom: 16 }}>{t('securityNote')}</h2>
                        <p style={{ fontSize: 15, opacity: 0.8, lineHeight: 1.6, marginBottom: 24 }}>
                            {t('secureSessionMsg')}
                        </p>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: 16, flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 10, textTransform: 'uppercase', opacity: 0.5, marginBottom: 4 }}>{t('status')}</div>
                                <div style={{ fontWeight: 800, color: 'var(--sindhi-gold)' }}>{t('activeLabel')}</div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: 16, flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 10, textTransform: 'uppercase', opacity: 0.5, marginBottom: 4 }}>{t('database')}</div>
                                <div style={{ fontWeight: 800 }}>Firebase</div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
